import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { EmailPreview } from '../components/EmailPreview';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { EmailBuilder } from '../components/builder/EmailBuilder';
import { EmailBlock, EmailGlobalStyles, EmailBuilderSchema, DEFAULT_EMAIL_STYLES } from '../components/builder/types';
import { emailTemplateLibraryApi, EmailLibraryRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
const CATEGORIES = ['Confirmation', 'Welcome', 'Notification', 'Newsletter', 'Other'];
export function EmailTemplateLibraryEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [templateData, setTemplateData] = useState({
    subject: '',
    preheader: '',
    html: ''
  });
  const [emailBuilderBlocks, setEmailBuilderBlocks] = useState<EmailBlock[]>([]);
  const [emailBuilderStyles, setEmailBuilderStyles] = useState<EmailGlobalStyles>(DEFAULT_EMAIL_STYLES);
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      emailTemplateLibraryApi.get(id).then(record => {
        if (record) {
          setTemplateName(record.Template_Name || '');
          setDescription(record.Description || '');
          setCategory(record.Category || '');
          setTemplateData({
            subject: record.Email_Subject || '',
            preheader: record.Preheader_Text || '',
            html: record.Email_HTML_Body || ''
          });
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: EmailBuilderSchema = JSON.parse(record.Builder_Schema);
              setEmailBuilderBlocks(schema.blocks || []);
              setEmailBuilderStyles(schema.globalStyles || DEFAULT_EMAIL_STYLES);
              setEditorMode('builder');
            } catch {
              /* fallback to code */
            }
          }
        } else {
          toast.error('Template not found');
          navigate('/email-templates-library');
        }
      }).catch(err => {
        toast.error('Failed to load template', {
          description: err instanceof Error ? err.message : 'Unknown error'
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template Name is required');
      return;
    }
    setIsSaving(true);
    const payload: Record<string, any> = {
      Template_Name: templateName,
      Description: description,
      Category: category,
      Email_HTML_Body: templateData.html,
      Email_Subject: templateData.subject,
      Preheader_Text: templateData.preheader,
      Builder_Mode: editorMode,
      Team: team
    };
    if (editorMode === 'builder') {
      payload.Builder_Schema = JSON.stringify({
        blocks: emailBuilderBlocks,
        globalStyles: emailBuilderStyles
      });
    }
    try {
      if (isEditing && id) {
        await emailTemplateLibraryApi.update(id, payload);
        toast.success('Email template updated');
      } else {
        await emailTemplateLibraryApi.create(payload);
        toast.success('Email template created');
        navigate('/email-templates-library');
      }
    } catch (err) {
      toast.error('Failed to save template', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-2561">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-2562" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-2563">Loading template...</span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-2564">
      <div className="flex items-center justify-between" data-id="element-2565">
        <div className="flex items-center gap-4" data-id="element-2566">
          <Link to="/email-templates-library" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-2567">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-2568" />
          </Link>
          <div data-id="element-2569">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2570">
              {isEditing ? 'Edit Email Template' : 'Create Email Template'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-2571">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your email.' : 'Write email HTML for this template.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-2572">
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-2573">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2574">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && templateData.html && emailBuilderBlocks.length === 0) {
              if (!confirm('Switching to Builder mode will start fresh. Continue?')) return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2575">
              Builder
            </button>
          </div>
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-2576">
            <EyeIcon className="h-4 w-4" data-id="element-2577" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-2578">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-2579" /> : <SaveIcon className="h-4 w-4" data-id="element-2580" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-2581">
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-2582">
            <div className="grid grid-cols-2 gap-4" data-id="element-2583">
              <div data-id="element-2584">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2585">
                  Email Subject
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.subject} onChange={e => setTemplateData({
              ...templateData,
              subject: e.target.value
            })} placeholder="Email subject line" data-id="element-2586" />
              </div>
              <div data-id="element-2587">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2588">
                  Preheader Text
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.preheader} onChange={e => setTemplateData({
              ...templateData,
              preheader: e.target.value
            })} placeholder="Brief summary..." data-id="element-2589" />
              </div>
            </div>
          </div>

          <EmailBuilder initialBlocks={emailBuilderBlocks} initialStyles={emailBuilderStyles} onSchemaChange={(blocks, globalStyles) => {
        setEmailBuilderBlocks(blocks);
        setEmailBuilderStyles(globalStyles);
      }} onHtmlChange={html => {
        setTemplateData(prev => ({
          ...prev,
          html
        }));
      }} mergeVariables={['confirmation_link', 'team_name', 'first_name', 'last_name', 'email']} data-id="element-2590" />

          <div className="grid grid-cols-3 gap-6" data-id="element-2591">
            <div className="col-span-2" data-id="element-2592" />
            <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} data-id="element-2593" />
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-2594">
          <div className="col-span-2 space-y-6" data-id="element-2595">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4" data-id="element-2596">
              <div className="grid grid-cols-2 gap-4" data-id="element-2597">
                <div className="space-y-2" data-id="element-2598">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2599">
                    Email Subject
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.subject} onChange={e => setTemplateData({
                ...templateData,
                subject: e.target.value
              })} placeholder="Email subject line" data-id="element-2600" />
                </div>
                <div className="space-y-2" data-id="element-2601">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2602">
                    Preheader Text
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.preheader} onChange={e => setTemplateData({
                ...templateData,
                preheader: e.target.value
              })} placeholder="Brief summary..." data-id="element-2603" />
                </div>
              </div>
              <CodeEditor label="Email HTML Body" language="html" className="min-h-[400px]" value={templateData.html} onChange={e => setTemplateData({
            ...templateData,
            html: e.target.value
          })} data-id="element-2604" />
            </div>
            <GptCodeAssistant mode="email" currentCode={{
          html: templateData.html
        }} onCodeGenerated={code => {
          if (code.html) {
            setTemplateData(prev => ({
              ...prev,
              html: code.html
            }));
          }
        }} data-id="element-2605" />
          </div>
          <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} data-id="element-2606" />
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Email Template Preview" data-id="element-2607">
        <EmailPreview subject={templateData.subject} html={templateData.html} data-id="element-2608" />
      </PreviewModal>
    </div>;
}
function SettingsPanel({
  templateName,
  setTemplateName,
  description,
  setDescription,
  category,
  setCategory,
  team
}: {
  templateName: string;
  setTemplateName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  team: string;
}) {
  return <div className="space-y-6" data-id="element-2609">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-2610">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-2611">
          Template Settings
        </h3>
        <div className="space-y-2" data-id="element-2612">
          <label className="block text-sm font-medium text-gray-700" data-id="element-2613">
            Template Name
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="My Email Template" data-id="element-2614" />
        </div>
        <div className="space-y-2" data-id="element-2615">
          <label className="block text-sm font-medium text-gray-700" data-id="element-2616">
            Description
          </label>
          <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what this template is for..." data-id="element-2617" />
        </div>
        <div className="space-y-2" data-id="element-2618">
          <label className="block text-sm font-medium text-gray-700" data-id="element-2619">
            Category
          </label>
          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={category} onChange={e => setCategory(e.target.value)} data-id="element-2620">
            <option value="" data-id="element-2621">Select category...</option>
            {CATEGORIES.map(c => <option key={c} value={c} data-id="element-2622">
                {c}
              </option>)}
          </select>
        </div>
        <div className="space-y-2" data-id="element-2623">
          <label className="block text-sm font-medium text-gray-700" data-id="element-2624">
            Team
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-2625" />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-2626">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-2627">
          Available Variables
        </h3>
        <p className="text-xs text-gray-500" data-id="element-2628">
          Use these merge tags in your email HTML. They'll be replaced with
          actual values when the email is sent.
        </p>
        <div className="flex flex-wrap gap-1.5" data-id="element-2629">
          {['confirmation_link', 'team_name', 'first_name', 'last_name', 'email'].map(v => <button key={v} onClick={() => {
          const text = `{{${v}}}`;
          if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
            toast.success(`Copied ${text}`, {
              duration: 1500
            });
          }
        }} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100" title={`Copy {{${v}}}`} data-id="element-2630">
              {`{{${v}}}`}
            </button>)}
        </div>
      </div>
    </div>;
}